import React, { useRef } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { Button, useToast } from '@morressier/alembic';
import { isEqual, cloneDeep } from 'lodash';
import { Flex } from '@chakra-ui/react';
import { Overlay } from 'src/components/Overlay';
import { ISubmissionFormData } from 'src/contexts/FormContext';
import { IAuthorWithEmail } from '@publishing-engine/domain/models';
import { AuthorsForm } from 'src/components/Submission/GenericFieldComponents/Authors/AuthorsForm';
import { IAuthorStepConfig } from 'src/hooks/useSubmissionStepsConfig';
import { IAuthorRolesMap } from 'src/utils/groupAuthorByRoles';

interface Props {
  onClose: () => void;
  config: IAuthorStepConfig;
}

const AuthorListEditModal: React.FC<Props> = props => {
  const { onClose, config } = props;

  // hooks
  const toast = useToast();
  const methods = useFormContext<ISubmissionFormData>();

  const {
    getValues,
    setValue,
    formState: { errors }
  } = methods;
  const authors = getValues('metadata.authors');
  const submittingAuthor = getValues('submittingAuthor');
  const contactAuthor = getValues('contactAuthor');
  const correspondingAuthor = getValues('correspondingAuthor');

  const authorRoleMap: IAuthorRolesMap = {
    Submitting: submittingAuthor,
    ...(contactAuthor && {
      Contact: contactAuthor
    }),
    ...(correspondingAuthor && {
      Corresponding: correspondingAuthor
    })
  };

  const initialAuthorsData = useRef<IAuthorWithEmail[]>(
    cloneDeep(authors || [])
  );
  const errorObject = errors.metadata?.authors;
  const hasValidationErrors = !!errorObject;
  const handleClose = () => {
    const currentAuthorsData = getValues('metadata.authors');
    const hasChanges = !isEqual(initialAuthorsData.current, currentAuthorsData);

    if (hasChanges) {
      setValue('showDiscardChangesModal', true as any);
    } else {
      onClose();
    }
  };

  const handleSave = async () => {
    const authors = getValues('metadata.authors');
    if (authors && authors.length === 0) {
      setValue('metadata.authors', [], { shouldValidate: true });
    }

    initialAuthorsData.current = cloneDeep(getValues('metadata.authors'));
    onClose();
    toast({
      variant: 'success',
      title: 'Changes saved'
    });
  };

  return (
    <FormProvider {...methods}>
      <Overlay
        height="100%"
        data-cy="author-list-edit-modal"
        overflow={'hidden'}
      >
        {/* ISSUE: When user open the overlay and they scroll to bottom they can see the page behind the overlay.
            
            NOTE: The height 100% in Overlay and below Flex component,
            is temporary solution to cover available height for overlay. Doing that we can hide the page behind the overlay.
        */}
        <Flex
          // minHeight="100vh" // this line commented because of overlay issue
          height="100%"
          width="100vw"
          flexDirection="column"
          backgroundColor="surfaceSecondaryFill"
        >
          <AuthorsForm
            config={config.Authors}
            onClose={onClose}
            value={initialAuthorsData.current}
            authorRoleMap={authorRoleMap}
          />

          <Flex
            gap={2}
            justifyContent={{ base: 'stretch', md: 'flex-end' }}
            width={'100%'}
            backgroundColor="surfacePrimaryFill"
            position={'sticky'}
            bottom={0}
            paddingX={{ base: 4, md: 16 }}
            paddingY={{ base: 3, md: 4 }}
          >
            <Flex
              width="100%"
              justifyContent="space-between"
              direction="row"
              gap={2}
              alignItems="center"
            >
              <Button
                variant="caution"
                onClick={() => {
                  setValue('metadata.authors', initialAuthorsData.current);
                  onClose();
                }}
              >
                Exit
              </Button>
              <Flex gap={2}>
                <Button
                  onClick={handleClose}
                  variant="secondary"
                  data-cy="close-button"
                >
                  Close
                </Button>
                <Button
                  type="submit"
                  onClick={handleSave}
                  data-cy="save-button"
                  disabled={hasValidationErrors}
                >
                  Save Changes
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Overlay>
    </FormProvider>
  );
};

export default AuthorListEditModal;
